import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  Workspace,
  Space,
  List,
  Task,
  KanbanViewState,
  TasksByStatus,
  TaskPriority,
  ViewType,
} from "@/models/kanban";

// ============================================================================
// Types
// ============================================================================

interface KanbanState {
  // Current context
  currentWorkspace: Workspace | null;
  currentSpace: Space | null;
  currentList: List | null;

  // Navigation
  workspaces: Workspace[];
  spaces: Space[];
  lists: List[];

  // View state
  viewState: KanbanViewState | null;
  currentView: ViewType;

  // Task data
  tasksByStatus: TasksByStatus;
  selectedTaskId: string | null;
  expandedSubtasks: Record<string, boolean>;

  // UI state
  isTaskModalOpen: boolean;
  isCreateTaskModalOpen: boolean;
  isWorkspaceSettingsOpen: boolean;
  isSidebarCollapsed: boolean;

  // Drag state
  draggedTaskId: string | null;
  dragOverStatusId: string | null;

  // Loading states
  isLoading: boolean;
  isLoadingTasks: boolean;
  error: string | null;

  // Filters
  filterPriority: TaskPriority | null;
  filterAssigneeId: string | null;
  searchQuery: string;
  showCompletedTasks: boolean;
}

const initialState: KanbanState = {
  currentWorkspace: null,
  currentSpace: null,
  currentList: null,
  workspaces: [],
  spaces: [],
  lists: [],
  viewState: null,
  currentView: "kanban",
  tasksByStatus: {},
  selectedTaskId: null,
  expandedSubtasks: {},
  isTaskModalOpen: false,
  isCreateTaskModalOpen: false,
  isWorkspaceSettingsOpen: false,
  isSidebarCollapsed: false,
  draggedTaskId: null,
  dragOverStatusId: null,
  isLoading: false,
  isLoadingTasks: false,
  error: null,
  filterPriority: null,
  filterAssigneeId: null,
  searchQuery: "",
  showCompletedTasks: true,
};

// ============================================================================
// Slice
// ============================================================================

export const kanbanSlice = createSlice({
  name: "kanban",
  initialState,
  reducers: {
    // Context setters
    setCurrentWorkspace: (state, action: PayloadAction<Workspace | null>) => {
      state.currentWorkspace = action.payload;
      // Reset child context when workspace changes
      state.currentSpace = null;
      state.currentList = null;
      state.spaces = [];
      state.lists = [];
      state.tasksByStatus = {};
    },

    setCurrentSpace: (state, action: PayloadAction<Space | null>) => {
      state.currentSpace = action.payload;
      // Reset child context when space changes
      state.currentList = null;
      state.lists = [];
      state.tasksByStatus = {};
    },

    setCurrentList: (state, action: PayloadAction<List | null>) => {
      state.currentList = action.payload;
      state.tasksByStatus = {};
    },

    // Navigation data
    setWorkspaces: (state, action: PayloadAction<Workspace[]>) => {
      state.workspaces = action.payload;
    },

    setSpaces: (state, action: PayloadAction<Space[]>) => {
      state.spaces = action.payload;
    },

    setLists: (state, action: PayloadAction<List[]>) => {
      state.lists = action.payload;
    },

    // Task data
    setTasksByStatus: (state, action: PayloadAction<TasksByStatus>) => {
      state.tasksByStatus = action.payload;
    },

    // Optimistic update for task move
    moveTaskOptimistic: (
      state,
      action: PayloadAction<{
        taskId: string;
        sourceStatusId: string;
        targetStatusId: string;
        targetIndex: number;
      }>
    ) => {
      const { taskId, sourceStatusId, targetStatusId, targetIndex } = action.payload;

      // Find and remove task from source
      const sourceColumn = state.tasksByStatus[sourceStatusId] || [];
      const taskIndex = sourceColumn.findIndex((t) => t.id === taskId);
      if (taskIndex === -1) return;

      const [task] = sourceColumn.splice(taskIndex, 1);
      task.statusId = targetStatusId;

      // Add to target at specific index
      const targetColumn = state.tasksByStatus[targetStatusId] || [];
      targetColumn.splice(targetIndex, 0, task);

      // Update order values
      targetColumn.forEach((t, idx) => {
        t.orderInStatus = idx;
      });

      state.tasksByStatus[sourceStatusId] = sourceColumn;
      state.tasksByStatus[targetStatusId] = targetColumn;
    },

    // Revert optimistic update
    revertTaskMove: (
      state,
      action: PayloadAction<{
        taskId: string;
        originalStatusId: string;
        originalIndex: number;
        currentStatusId: string;
      }>
    ) => {
      const { taskId, originalStatusId, originalIndex, currentStatusId } = action.payload;

      // Find and remove from current position
      const currentColumn = state.tasksByStatus[currentStatusId] || [];
      const taskIndex = currentColumn.findIndex((t) => t.id === taskId);
      if (taskIndex === -1) return;

      const [task] = currentColumn.splice(taskIndex, 1);
      task.statusId = originalStatusId;

      // Add back to original position
      const originalColumn = state.tasksByStatus[originalStatusId] || [];
      originalColumn.splice(originalIndex, 0, task);

      state.tasksByStatus[originalStatusId] = originalColumn;
      state.tasksByStatus[currentStatusId] = currentColumn;
    },

    // Add task to column (for optimistic create)
    addTaskToColumn: (state, action: PayloadAction<Task>) => {
      const task = action.payload;
      const column = state.tasksByStatus[task.statusId] || [];
      column.push(task);
      state.tasksByStatus[task.statusId] = column;
    },

    // Remove task from column (for optimistic delete)
    removeTaskFromColumn: (
      state,
      action: PayloadAction<{ taskId: string; statusId: string }>
    ) => {
      const { taskId, statusId } = action.payload;
      const column = state.tasksByStatus[statusId] || [];
      state.tasksByStatus[statusId] = column.filter((t) => t.id !== taskId);
    },

    // Update task in place
    updateTaskInColumn: (state, action: PayloadAction<Task>) => {
      const task = action.payload;
      const column = state.tasksByStatus[task.statusId] || [];
      const index = column.findIndex((t) => t.id === task.id);
      if (index !== -1) {
        column[index] = task;
      }
    },

    // View state
    setViewState: (state, action: PayloadAction<KanbanViewState | null>) => {
      state.viewState = action.payload;
    },

    setCurrentView: (state, action: PayloadAction<ViewType>) => {
      state.currentView = action.payload;
    },

    // Selection
    setSelectedTaskId: (state, action: PayloadAction<string | null>) => {
      state.selectedTaskId = action.payload;
      state.isTaskModalOpen = action.payload !== null;
    },

    toggleSubtaskExpanded: (state, action: PayloadAction<string>) => {
      const taskId = action.payload;
      state.expandedSubtasks[taskId] = !state.expandedSubtasks[taskId];
    },

    // Modal states
    openTaskModal: (state, action: PayloadAction<string>) => {
      state.selectedTaskId = action.payload;
      state.isTaskModalOpen = true;
    },

    closeTaskModal: (state) => {
      state.isTaskModalOpen = false;
      state.selectedTaskId = null;
    },

    openCreateTaskModal: (state) => {
      state.isCreateTaskModalOpen = true;
    },

    closeCreateTaskModal: (state) => {
      state.isCreateTaskModalOpen = false;
    },

    toggleWorkspaceSettings: (state) => {
      state.isWorkspaceSettingsOpen = !state.isWorkspaceSettingsOpen;
    },

    toggleSidebar: (state) => {
      state.isSidebarCollapsed = !state.isSidebarCollapsed;
    },

    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isSidebarCollapsed = action.payload;
    },

    // Drag state
    setDraggedTaskId: (state, action: PayloadAction<string | null>) => {
      state.draggedTaskId = action.payload;
    },

    setDragOverStatusId: (state, action: PayloadAction<string | null>) => {
      state.dragOverStatusId = action.payload;
    },

    // Loading states
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setIsLoadingTasks: (state, action: PayloadAction<boolean>) => {
      state.isLoadingTasks = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Filters
    setFilterPriority: (state, action: PayloadAction<TaskPriority | null>) => {
      state.filterPriority = action.payload;
    },

    setFilterAssigneeId: (state, action: PayloadAction<string | null>) => {
      state.filterAssigneeId = action.payload;
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    setShowCompletedTasks: (state, action: PayloadAction<boolean>) => {
      state.showCompletedTasks = action.payload;
    },

    clearFilters: (state) => {
      state.filterPriority = null;
      state.filterAssigneeId = null;
      state.searchQuery = "";
    },

    // Reset
    resetKanbanState: () => initialState,
  },
});

export const {
  setCurrentWorkspace,
  setCurrentSpace,
  setCurrentList,
  setWorkspaces,
  setSpaces,
  setLists,
  setTasksByStatus,
  moveTaskOptimistic,
  revertTaskMove,
  addTaskToColumn,
  removeTaskFromColumn,
  updateTaskInColumn,
  setViewState,
  setCurrentView,
  setSelectedTaskId,
  toggleSubtaskExpanded,
  openTaskModal,
  closeTaskModal,
  openCreateTaskModal,
  closeCreateTaskModal,
  toggleWorkspaceSettings,
  toggleSidebar,
  setSidebarCollapsed,
  setDraggedTaskId,
  setDragOverStatusId,
  setIsLoading,
  setIsLoadingTasks,
  setError,
  setFilterPriority,
  setFilterAssigneeId,
  setSearchQuery,
  setShowCompletedTasks,
  clearFilters,
  resetKanbanState,
} = kanbanSlice.actions;

export default kanbanSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

export const selectCurrentWorkspace = (state: { kanbanReducer: KanbanState }) =>
  state.kanbanReducer.currentWorkspace;

export const selectCurrentSpace = (state: { kanbanReducer: KanbanState }) =>
  state.kanbanReducer.currentSpace;

export const selectCurrentList = (state: { kanbanReducer: KanbanState }) =>
  state.kanbanReducer.currentList;

export const selectTasksByStatus = (state: { kanbanReducer: KanbanState }) =>
  state.kanbanReducer.tasksByStatus;

export const selectFilteredTasksByStatus = (state: { kanbanReducer: KanbanState }) => {
  const { tasksByStatus, filterPriority, filterAssigneeId, searchQuery, showCompletedTasks } =
    state.kanbanReducer;

  const filtered: TasksByStatus = {};

  Object.entries(tasksByStatus).forEach(([statusId, tasks]) => {
    let filteredTasks = tasks;

    // Filter by priority
    if (filterPriority) {
      filteredTasks = filteredTasks.filter((t) => t.priority === filterPriority);
    }

    // Filter by assignee
    if (filterAssigneeId) {
      filteredTasks = filteredTasks.filter((t) =>
        t.assignees.some((a) => a.userId === filterAssigneeId)
      );
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredTasks = filteredTasks.filter(
        (t) =>
          t.title.toLowerCase().includes(query) ||
          t.description?.toLowerCase().includes(query) ||
          t.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    // Filter completed tasks
    if (!showCompletedTasks && statusId === "done") {
      filteredTasks = [];
    }

    filtered[statusId] = filteredTasks;
  });

  return filtered;
};

export const selectSelectedTask = (state: { kanbanReducer: KanbanState }) => {
  const { tasksByStatus, selectedTaskId } = state.kanbanReducer;
  if (!selectedTaskId) return null;

  for (const tasks of Object.values(tasksByStatus)) {
    const task = tasks.find((t) => t.id === selectedTaskId);
    if (task) return task;
  }
  return null;
};

export const selectIsTaskModalOpen = (state: { kanbanReducer: KanbanState }) =>
  state.kanbanReducer.isTaskModalOpen;

export const selectDraggedTask = (state: { kanbanReducer: KanbanState }) => {
  const { tasksByStatus, draggedTaskId } = state.kanbanReducer;
  if (!draggedTaskId) return null;

  for (const tasks of Object.values(tasksByStatus)) {
    const task = tasks.find((t) => t.id === draggedTaskId);
    if (task) return task;
  }
  return null;
};

export const selectHasActiveFilters = (state: { kanbanReducer: KanbanState }) => {
  const { filterPriority, filterAssigneeId, searchQuery } = state.kanbanReducer;
  return !!(filterPriority || filterAssigneeId || searchQuery);
};
